import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Alert, Button, CloseIcon, Dialog, Flex, Input, Loader, SearchIcon, Segment, Text } from '@fluentui/react-northstar';
import Axios from 'axios';
import { Persona, PersonaSize } from '@fluentui/react';
import AccountRemoveIcon from 'mdi-react/AccountRemoveIcon';
import AccountPlusIcon from 'mdi-react/AccountPlusIcon';
import { GetContactsDTO } from '../../../../models/Administration/ContactController/getContactsDTO';
import AlertError, { IAlertErrorProps } from '../../../../components/alerts/AlertError';
import styles from './CANCOMProducts.module.scss';
import { GetProductsDTO } from '../../../../models/Administration/CANCOMProductController/getProductsDTO';
import { GetProductContactsDTO } from '../../../../models/CANCOM/getProductContactsDTO';
import { LocalizationService } from '../../../../services/LocalizationService';

interface ICANCOMProductContactsDialogProps extends WithTranslation {
    onClose?: any;
    product: GetProductsDTO;
}

interface ICANCOMProductContactsDialogState {
    error: string | null;
    assigned: { loading: boolean, contacts?: GetProductContactsDTO[], error: IAlertErrorProps | null };
    assignedContactRemoved: GetProductContactsDTO | null;
    available: { loading: boolean, contacts?: GetContactsDTO[], error: IAlertErrorProps | null };
    search: { input: string, contacts: GetProductContactsDTO[] };
}

class CANCOMProductContactsDialog extends React.Component<ICANCOMProductContactsDialogProps, ICANCOMProductContactsDialogState> {

    public state: ICANCOMProductContactsDialogState = {
        error: null,
        assigned: { loading: true, error: null },
        assignedContactRemoved: null,
        available: { loading: true, error: null },
        search: { input: '', contacts: [] },
    }

    public async componentDidMount() {
        this.getAssignedContacts();
        this.getAvailableContacts();
    }

    public getAssignedContacts = async () => {
        try {
            this.setState({ assigned: { loading: true, error: null } });
            const response = await Axios.get(window.env.REACT_APP_API_BASE + '/api/admin/cancom/products/' + this.props.product.id + '/contacts');
            this.setState({ assigned: { loading: false, contacts: response.data, error: null } });
            this.onSearch(this.state.search.input);
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.errorCode) {
                this.setState({ assigned: { loading: false, error: { message: error.response.data.errorCode, retry: () => this.getAssignedContacts() } } });
            } else {
                this.setState({ assigned: { loading: false, error: { message: 'error.admin.cancom.products.loadassignedcontacts', retry: () => this.getAssignedContacts() } } });
            }
        }
    }

    public getAvailableContacts = async () => {
        try {
            this.setState({ available: { loading: true, error: null } });
            const response = await Axios.get(window.env.REACT_APP_API_BASE + '/api/admin/contacts');
            this.setState({ available: { loading: false, contacts: response.data, error: null } });
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.errorCode) {
                this.setState({ available: { loading: false, error: { message: error.response.data.errorCode, retry: () => this.getAvailableContacts() } } });
            } else {
                this.setState({ available: { loading: false, error: { message: 'error.admin.cancom.products.loadavailablecontacts', retry: () => this.getAvailableContacts() } } });
            }
        }
    }

    public onSearch = (searchInput: string) => {
        const searchRegex: RegExp = new RegExp('.*' + (searchInput || '').replace(/\s+/gi, '.*') + '.*', 'gi');
        let searchResults: GetContactsDTO[] = [];
        if (this.state.assigned.contacts && this.state.available.contacts && searchInput && searchInput.length >= 2) {
            searchResults = this.state.available.contacts.filter((contact: GetContactsDTO) => this.state.assigned.contacts && !this.state.assigned.contacts.some((productContact: GetProductContactsDTO) => productContact.id === contact.id) && (contact.firstname?.match(searchRegex) || contact.lastname?.match(searchRegex) || contact.mail?.match(searchRegex)));
        }
        this.setState({ search: { input: searchInput, contacts: searchResults.slice(0, 10) } });
    }

    public onAddContact = async (contact: GetContactsDTO | GetProductContactsDTO | null) => {
        try {
            if (contact === null) return;
            this.setState({ assigned: { ...this.state.assigned, loading: true }, assignedContactRemoved: null });
            await Axios.put(window.env.REACT_APP_API_BASE + '/api/admin/cancom/products/' + this.props.product.id + '/contacts/' + contact?.id);
            this.getAssignedContacts();
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.errorCode) {
                this.setState({ assigned: { ...this.state.assigned, loading: false, error: { message: error.response.data.errorCode, retry: () => this.onAddContact(contact) } } });
            } else {
                this.setState({ assigned: { ...this.state.assigned, loading: false, error: { message: 'error.admin.cancom.products.addcontact', retry: () => this.onAddContact(contact) } } });
            }
        }
    }

    public onRemoveContact = async (contact: GetProductContactsDTO) => {
        try {
            this.setState({ assigned: { ...this.state.assigned, loading: true }, assignedContactRemoved: null });
            await Axios.delete(window.env.REACT_APP_API_BASE + '/api/admin/cancom/products/' + this.props.product.id + '/contacts/' + contact.id);
            this.setState({ assignedContactRemoved: contact });
            this.getAssignedContacts();
        } catch (error: any) {
            console.error(error);
            if (error?.response?.data?.errorCode) {
                this.setState({ assigned: { ...this.state.assigned, loading: false, error: { message: error.response.data.errorCode, retry: () => this.onRemoveContact(contact) } } });
            } else {
                this.setState({ assigned: { ...this.state.assigned, loading: false, error: { message: 'error.admin.cancom.products.removecontact', retry: () => this.onRemoveContact(contact) } } });
            }
        }
    }

    render() {

        const form = (
            <div className={styles.CANCOMProductContactsDialog}>
                <Flex gap="gap.small" style={{ marginTop: '8px' }}>
                    <div style={{ width: '50%' }}>
                        <Segment style={{ marginBottom: '1px' }}>
                            <strong>{this.props.t('admin.cancom.products.dialog.contacts.sections.assigned.title')}</strong>
                        </Segment>
                        <Segment className="assigned">
                            {!this.state.assigned.loading && this.state.assigned.error ? <AlertError {...this.state.assigned.error} /> : null}
                            {this.state.assigned.loading ? <Loader /> : <React.Fragment>
                                {this.state.assignedContactRemoved ? <Alert warning={true} className="removed">
                                    {this.props.t('admin.cancom.products.dialog.contacts.sections.assigned.removed', { name: this.state.assignedContactRemoved.firstname + ' ' + this.state.assignedContactRemoved.lastname })}
                                    <Text weight="semibold" className="revert" onClick={() => this.onAddContact(this.state.assignedContactRemoved)}>
                                        {this.props.t('admin.cancom.products.dialog.contacts.sections.assigned.revert')}
                                    </Text>
                                </Alert> : null}
                                {this.state.assigned.contacts && this.state.assigned.contacts.length === 0 ? <div>{this.props.t('admin.cancom.products.dialog.contacts.sections.assigned.empty')}</div> : null}
                                {this.state.assigned.contacts && this.state.assigned.contacts.length > 0 ? this.state.assigned.contacts.map((contact: GetProductContactsDTO) => (
                                    <Flex key={contact.id} space="between" vAlign="center" className="item">
                                        <Persona text={`${contact.firstname} ${contact.lastname}`} secondaryText={contact.mail} showSecondaryText={true} size={PersonaSize.size32} />
                                        <Button size="small" icon={<AccountRemoveIcon />} content={this.props.t('admin.cancom.products.dialog.contacts.sections.assigned.remove')} onClick={() => this.onRemoveContact(contact)} />
                                    </Flex>
                                )) : null}
                            </React.Fragment>}
                        </Segment>
                    </div>
                    <div style={{ width: '50%' }}>
                        <Segment style={{ marginBottom: '1px' }}>
                            <strong>{this.props.t('admin.cancom.products.dialog.contacts.sections.available.title')}</strong>
                        </Segment>
                        <Segment className="available">
                            {!this.state.available.loading && this.state.available.error ? <AlertError {...this.state.available.error} /> : null}
                            {!this.state.available.error ? <div>
                                <Input fluid={true} clearable={true} autoComplete="off" autoCorrect="off" icon={<SearchIcon />} placeholder={this.props.t('admin.cancom.products.dialog.contacts.sections.available.search')}
                                    disabled={this.state.available.loading} value={this.state.search.input} onChange={(evt: any) => this.onSearch(evt.target?.value)} />
                            </div> : null}
                            {this.state.search.contacts.map((contact: GetContactsDTO) => (
                                <Flex key={contact.id} space="between" vAlign="center" style={{ marginTop: '12px' }}>
                                    <Persona text={`${contact.firstname} ${contact.lastname}`} secondaryText={contact.mail} showSecondaryText={true} size={PersonaSize.size32} />
                                    <Button size="small" icon={<AccountPlusIcon />} content={this.props.t('admin.cancom.products.dialog.contacts.sections.available.add')} onClick={() => this.onAddContact(contact)} />
                                </Flex>
                            ))}
                        </Segment>
                    </div>
                </Flex>
            </div>
        );

        return (
            <Dialog
                open={true}
                closeOnOutsideClick={false}
                content={form}
                header={this.props.t('admin.cancom.products.dialog.contacts.title', { title: LocalizationService.getLocalizedString(this.props.product.title, this.props.i18n.language) })}
                headerAction={{ icon: <CloseIcon />, title: this.props.t('admin.cancom.products.dialog.common.close'), onClick: () => this.props.onClose && this.props.onClose() }} />
        );

    }

}

export default withTranslation()(CANCOMProductContactsDialog);